import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from './customFetcher';
import { z } from 'zod';

export const profileSchema = z.object({
    id: z.number(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
    profile_picture: z.string().nullable().optional(),
    status: z.number().nullable().optional(),
    strasse: z.string().nullable().optional(),
    hausnummer: z.string().nullable().optional(),
    plz: z.string().nullable().optional(),
    stadt: z.string().nullable().optional(),
    land: z.string().nullable().optional(),
    geburtstag: z.string().nullable().optional(),
    telefon: z.string().nullable().optional(),
    mobil: z.string().nullable().optional(),
    beruf: z.string().nullable().optional(),
    biernamen: z.string().nullable().optional(),
    aemter: z.string().nullable().optional(),
    webseite: z.string().nullable().optional(),
    arbeitgeber: z.string().nullable().optional(),
    xing: z.string().nullable().optional(),
    linkedin: z.string().nullable().optional(),
    facebook: z.string().nullable().optional(),
    instagram: z.string().nullable().optional(),
    eintritt: z.string().nullable().optional(),
});

export type IProfile = z.infer<typeof profileSchema>;

const PROFILE_KEY = ['profile', 'me'];

export const useProfile = (enabled = true) => {
    const { data, isLoading } = useQuery<IProfile>({
        queryKey: PROFILE_KEY,
        enabled,
        queryFn: async () => {
            const raw = await customFetcher<unknown>({ endpoint: '/accounts/me/' });
            return profileSchema.parse(raw);
        },
        staleTime: 30_000,
    });
    return { profile: data ?? null, isLoading };
};

export const useUpdateProfile = () => {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } = useMutation<IProfile, Error, Partial<IProfile>>({
        mutationFn: (patch) =>
            customFetcher<IProfile>({ endpoint: '/accounts/me/', method: 'PATCH', body: patch }),
        onSuccess: (data) => {
            queryClient.setQueryData(PROFILE_KEY, data);
        },
    });
    return { updateProfile: mutateAsync, isUpdating: isPending };
};
